import { DPR } from '@constants/common.const';
import Phaser from 'phaser';
import type { ModalSheetState, ModalSheetConfig } from './modal-sheet.types';

export class ModalSheetGeometry<TemplateData> {
  public sheetWidth = 0;
  public collapsedY = 0;
  public expandedY = 0;
  public hiddenY = 0;
  public collapsedHeight = 0;
  public expandedHeight = 0;

  public readonly padding = {
    left: 16 * DPR,
    right: 16 * DPR,
    top: (12 + 32) * DPR,
    bottom: 32 * DPR,
  };

  constructor(
    private scene: Phaser.Scene,
    private cfg: Required<ModalSheetConfig<TemplateData>>,
  ) {}

  public recalculate(): void {
    const w = this.scene.scale.width;
    const h = this.scene.scale.height;

    this.sheetWidth = Math.min(this.cfg.width, w);
    this.collapsedHeight = Math.min(this.cfg.collapsedHeight, h - 20);
    this.expandedHeight = Math.min(this.cfg.expandedHeight, h);

    this.hiddenY = h + 10;
    this.collapsedY = h - this.collapsedHeight;
    this.expandedY = h - this.expandedHeight;
  }

  public getXAligned(): number {
    return Math.floor((this.scene.scale.width - this.sheetWidth) / 2);
  }

  public getYForState(state: ModalSheetState): number {
    switch (state) {
      case 'hidden':
        return this.hiddenY;
      case 'collapsed':
        return this.collapsedY;
      case 'expanded':
        return this.expandedY;
      default:
        return this.hiddenY;
    }
  }

  public getHeightForState(state: ModalSheetState): number {
    return state === 'expanded' ? this.expandedHeight : this.collapsedHeight;
  }

  public measureContentHeight(container: Phaser.GameObjects.Container): number {
    let maxBottom = 0;
    for (const child of container.list) {
      let h = 0;

      // FIX: Specifically check for DOM element height
      if (child instanceof Phaser.GameObjects.DOMElement) {
        h = child.node.getBoundingClientRect().height * child.scale;
      } else {
        const go = child as Phaser.GameObjects.Container;
        h = go.displayHeight ?? go.height ?? 0;
      }

      const y = (child as Phaser.GameObjects.Container).y ?? 0;
      const originY = (child as Phaser.GameObjects.Container).originY ?? 0;
      const top = y - h * originY;
      const bottom = top + h;
      if (bottom > maxBottom) maxBottom = bottom;
    }
    return maxBottom;
  }

  public getViewportDimensions(state: ModalSheetState): {
    width: number;
    height: number;
  } {
    const w = Math.min(this.cfg.width, this.scene.scale.width);
    const visibleSheetH = this.getHeightForState(state);
    const width = w - this.padding.left - this.padding.right;
    const height = Math.max(0, visibleSheetH - this.padding.top);
    return { width, height };
  }
}
